import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Paper, Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import {
  ViewAgendaRounded, CheckCircleRounded, PersonRounded, TimerRounded, BusinessRounded, CategoryRounded, EditRounded,
} from '@mui/icons-material';
import { Crumbs, DetailHead, TabBar, KV, StatBox, SectionCard, Grid, useTab } from '../../components/common';
import DataTable, { Column } from '../../components/DataTable';
import StatCard from '../../components/StatCard';
import StatusChip from '../../components/StatusChip';
import QueueFormDialog, { QueueFormInitial } from '../../components/QueueFormDialog';
import { Avatar } from '../../components/NameCell';
import { useQueueDetail } from '../../hooks/useQueues';
import { utilPct, barColor, statusLabel } from '../../utils/format';
import { colors } from '../../theme/theme';

const fmt = (v: any, suffix = '') => (v === null || v === undefined || v === '' ? '—' : `${v}${suffix}`);

const QueueDetailPage: React.FC = () => {
  const { id } = useParams();
  const { data, loading, error, reload } = useQueueDetail(id);
  const [tab, setTab] = useTab('overview');
  const [editOpen, setEditOpen] = useState(false);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>;
  }
  if (error || !data) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error || 'Not found.'}</Alert>;
  }

  const q = data.queue;
  const capacity = Number(q.capacity || 0);
  const booked = Number(q.booked || 0);
  const pct = utilPct(booked, capacity);

  const editInitial: QueueFormInitial = {
    id: String(q.id),
    name: String(q.name || ''),
    business_id: String(q.business_id || ''),
    booking_mode: q.booking_mode ? String(q.booking_mode) : 'public',
    provider_user_ids: data.providers.map((p) => String(p.user_id)).filter(Boolean),
    schedule: data.schedule.map((r) => ({
      day: String(r.day),
      is_enabled: !!r.enabled,
      start_time: r.start ? String(r.start).slice(0, 5) : undefined,
      end_time: r.end ? String(r.end).slice(0, 5) : undefined,
      max_tokens: r.max_tokens ?? undefined,
    })),
  };

  const tokCols: Column<any>[] = [
    { key: 'token', label: 'Token #', render: (r) => <Typography component="span" sx={{ fontWeight: 600 }}>{r.token}</Typography> },
    { key: 'customer', label: 'Customer' },
    { key: 'type', label: 'Type' },
    { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} label={statusLabel(r.status)} /> },
    { key: 'check_in', label: 'Check-in' },
  ];
  const provCols: Column<any>[] = [
    { key: 'name', label: 'Provider', render: (r) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}><Avatar name={r.name} /><span>{r.name}</span></Box> },
    { key: 'role', label: 'Role' },
    { key: 'served', label: 'Served', align: 'right' },
  ];
  const schedCols: Column<any>[] = [
    { key: 'day', label: 'Day' },
    { key: 'hours', label: 'Hours', render: (r) => (r.enabled ? <span>{String(r.start).slice(0, 5)} – {String(r.end).slice(0, 5)}</span> : <Box component="span" sx={{ color: colors.error, fontWeight: 600 }}>Closed</Box>) },
    { key: 'max_tokens', label: 'Max Tokens', align: 'right', render: (r) => (r.enabled ? r.max_tokens : '—') },
  ];

  return (
    <>
      <Crumbs items={[{ label: 'Queues', to: '/queues' }, { label: q.name }]} />
      <DetailHead
        title={q.name}
        actions={
          <>
            <Button size="small" variant="outlined" startIcon={<EditRounded />} onClick={() => setEditOpen(true)}
              sx={{ borderColor: 'divider', color: 'text.primary' }}>
              Edit queue
            </Button>
            <StatusChip status={q.status} label={statusLabel(q.status)} />
          </>
        }
      />

      <Paper sx={{ p: 0, mb: 2.25, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1.3fr 1fr' }, '& > div': { p: 2.75 }, '& > div + div': { borderLeft: { md: `1px solid ${colors.border}` }, borderTop: { xs: `1px solid ${colors.border}`, md: 'none' } } }}>
        <Box>
          <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
            <Box sx={{ width: 62, height: 62, borderRadius: '16px', display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg,#076FF7,#065CC6)', color: '#fff', '& svg': { fontSize: 28 } }}>
              <ViewAgendaRounded />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 19, fontWeight: 800 }}>{q.name}</Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{q.business} · {q.category}</Typography>
              <Box sx={{ mt: 1.25 }}><StatusChip status={q.status} label={statusLabel(q.status)} /></Box>
            </Box>
          </Box>
        </Box>
        <Box>
          <KV k={<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}><BusinessRounded sx={{ fontSize: 15, color: colors.textTertiary }} /> Business</Box>} v={q.business} />
          <KV k={<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}><CategoryRounded sx={{ fontSize: 15, color: colors.textTertiary }} /> Category</Box>} v={q.category} />
          <KV k={<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}><PersonRounded sx={{ fontSize: 15, color: colors.textTertiary }} /> Providers</Box>} v={q.providers} />
          <KV k={<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}><ViewAgendaRounded sx={{ fontSize: 15, color: colors.textTertiary }} /> Queue ID</Box>} v={<Box component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{String(q.id).slice(0, 8)}</Box>} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Live Status</Typography>
          <StatBox>
            <KV dashed k="Capacity" v={capacity} />
            <KV dashed k="Booked" v={booked} />
            <KV dashed k="Utilization" v={`${pct}%`} />
            <KV k="Served Today" v={q.tokens_served_today} />
          </StatBox>
        </Box>
      </Paper>

      <Grid cols={5} sx={{ mb: 2.25 }}>
        <StatCard icon={<ViewAgendaRounded />} color="#076FF7" value={capacity} label="Capacity" />
        <StatCard icon={<CheckCircleRounded />} color="#16A34A" value={booked} label="Booked" />
        <StatCard icon={<PersonRounded />} color="#7C3AED" value={q.providers} label="Providers" />
        <StatCard icon={<TimerRounded />} color="#F59E0B" value={`${pct}%`} label="Utilization" />
        <StatCard icon={<TimerRounded />} color="#0EA5E9" value={fmt(q.avg_wait_time, ' min')} label="Avg Wait" />
      </Grid>

      <TabBar active={tab} onChange={setTab}
        tabs={[{ key: 'overview', label: 'Overview' }, { key: 'tokens', label: `Tokens (${data.tokens.length})` }, { key: 'providers', label: `Providers (${data.providers.length})` }, { key: 'schedule', label: 'Schedule' }]} />

      {tab === 'overview' && (
        <Grid cols={2}>
          <SectionCard title="Capacity">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, mb: 0.75 }}>
              <span>{booked} / {capacity} booked</span>
              <Typography component="span" sx={{ color: 'text.secondary', fontSize: 13 }}>{pct}%</Typography>
            </Box>
            <Box sx={{ height: 12, borderRadius: '99px', bgcolor: colors.gray100, overflow: 'hidden' }}>
              <Box sx={{ height: '100%', borderRadius: '99px', width: `${pct}%`, bgcolor: barColor(pct) }} />
            </Box>
            <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 1.5 }}>
              {capacity === 0 ? 'No slots configured for this queue yet.' : `This queue is ${pct > 80 ? 'near full capacity' : pct >= 50 ? 'moderately booked' : 'available for bookings'}.`}
            </Typography>
          </SectionCard>
          <SectionCard title="Queue Settings">
            <StatBox>
              <KV dashed k="Max Tokens / Slot" v={fmt(q.max_tokens_per_slot)} />
              <KV dashed k="Est. Service Time" v={fmt(q.estimated_service_time, ' min')} />
              <KV dashed k="Mapping Type" v={statusLabel(String(q.mapping_type || '—'))} />
              <KV dashed k="Booking Mode" v={statusLabel(String(q.booking_mode || '—'))} />
              <KV k="Queue Type" v={statusLabel(String(q.queue_type || '—'))} />
            </StatBox>
          </SectionCard>
        </Grid>
      )}
      {tab === 'tokens' && <Paper sx={{ p: 0 }}><DataTable columns={tokCols} rows={data.tokens} emptyText="No tokens." /></Paper>}
      {tab === 'providers' && <Paper sx={{ p: 0 }}><DataTable columns={provCols} rows={data.providers} emptyText="No providers assigned." /></Paper>}
      {tab === 'schedule' && <Paper sx={{ p: 0 }}><DataTable columns={schedCols} rows={data.schedule} emptyText="No schedule configured." pageSize={7} /></Paper>}

      <QueueFormDialog open={editOpen} mode="edit" onClose={() => setEditOpen(false)} onSaved={reload} initial={editInitial} />
    </>
  );
};

export default QueueDetailPage;
